import { useEffect, useMemo, useState } from 'react';
import { taskPriorities, taskPriorityLabels, taskTypeLabels } from '@brimo/shared';
import { Banner, Select } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { normalizeTimeInput } from '../../../lib/time-input';
import { useWorkspace } from '../../../lib/session';
import { createTask, snoozeTask, useAssignees } from '../api';
import { addDays, todayKey } from '../format';
import type { CreateTaskBody, TaskPriority } from '../types';
import { EditorCard, PrimaryAction } from './EditorCard';
import { ChipGroup, DateTimeBoxes, EditorField, validTime } from './fields';
import { isValidation, referencesOf, type EditorProps } from './shared';
import styles from './editor.module.css';

type TaskType = 'call' | 'email' | 'whatsapp' | 'document' | 'review';

const TYPES: ReadonlyArray<{ value: TaskType; label: string }> = (
  ['call', 'email', 'whatsapp', 'document', 'review'] as const
).map((value) => ({ value, label: taskTypeLabels[value] }));

const PRIORITIES = taskPriorities.map((value) => ({ value, label: taskPriorityLabels[value] }));

const MAX_CHIPS = 6;

interface Errors {
  title?: string | undefined;
  due?: string | undefined;
  assignee?: string | undefined;
}

export interface TaskEditorProps extends EditorProps {
  /** T5: only the due date moves (`POST /tasks/:id/snooze`); `task` is the task being moved. */
  mode?: 'create' | 'reschedule' | undefined;
}

/** Kontaktakte II.E5 „Aufgabe anlegen" and III.T5 „Termin verschieben". */
export function TaskEditor({ record, task, mode = 'create', onClose, onDone }: TaskEditorProps) {
  const workspace = useWorkspace();
  const reschedule = mode === 'reschedule' && task !== null;
  const assignees = useAssignees(!reschedule);
  const references = useMemo(() => referencesOf(record), [record]);
  const [title, setTitle] = useState('');
  const [type, setType] = useState<TaskType>('call');
  const [priority, setPriority] = useState<TaskPriority>('b');
  const [date, setDate] = useState(() => addDays(todayKey(), 1));
  const [time, setTime] = useState('');
  const [assigneeId, setAssigneeId] = useState(workspace.user.id);
  const [reference, setReference] = useState<string>(references[0]?.value ?? 'contact');
  const [errors, setErrors] = useState<Errors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!reschedule || !task) return;
    setTitle(task.title);
    if (task.due.on) setDate(task.due.on);
    setTime(task.due.time ?? '');
  }, [reschedule, task]);

  const users = assignees.data ?? [];
  const me = `${workspace.user.firstName} ${workspace.user.lastName}`.trim();
  const assigneeOptions =
    users.length > 0
      ? users.map((user) => ({ value: user.id, label: user.name }))
      : [{ value: workspace.user.id, label: me }];

  function validate(): boolean {
    const next: Errors = {};
    if (!reschedule && title.trim() === '') next.title = 'Bitte einen Titel angeben.';
    if (!date) next.due = 'Bitte ein Datum wählen.';
    else if (!validTime(time)) next.due = 'Bitte eine Uhrzeit im Format HH:MM angeben.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function submit() {
    if (!validate()) return;
    setPending(true);
    setBanner(null);
    try {
      if (reschedule && task) {
        const dueTime = normalizeTimeInput(time);
        await snoozeTask(task.id, dueTime ? { dueOn: date, dueTime } : { dueOn: date });
        onDone({ toast: 'Aufgabe verschoben.' });
        return;
      }
      const body: CreateTaskBody = {
        title: title.trim(),
        type,
        priority,
        dueOn: date,
        assigneeId,
        contactId: record.id,
      };
      if (time.trim()) body.dueTime = normalizeTimeInput(time);
      const chosen = references.find((item) => item.value === reference);
      if (chosen?.dealId) body.dealId = chosen.dealId;
      if (chosen?.ticketId) body.ticketId = chosen.ticketId;
      await createTask(body);
      onDone({ toast: 'Aufgabe angelegt.' });
    } catch (error) {
      if (isValidation(error)) {
        const fields = error.fields;
        setErrors({
          title: fields['title'],
          due: fields['dueOn'] ?? fields['dueTime'],
          assignee: fields['assigneeId'],
        });
        if (!fields['title'] && !fields['dueOn'] && !fields['dueTime'] && !fields['assigneeId'])
          setBanner(error.message);
      } else setBanner(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  return (
    <EditorCard
      tone="ink"
      dot="var(--accent)"
      title={reschedule ? 'Aufgabe verschieben' : 'Aufgabe anlegen'}
      subtitle={reschedule && task ? task.title : 'erscheint in Ihrer Task-Queue und in der Timeline'}
      label="Aufgaben-Editor"
      onClose={onClose}
      primary={
        <PrimaryAction loading={pending} onClick={() => void submit()}>
          {reschedule ? 'Verschieben' : 'Aufgabe anlegen'}
        </PrimaryAction>
      }
    >
      {banner ? <Banner tone="danger">{banner}</Banner> : null}
      {reschedule ? null : (
        <>
          <EditorField label="Titel" error={errors.title}>
            <input
              className={styles.input}
              aria-label="Titel"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              autoFocus
            />
          </EditorField>
          <EditorField label="Art">
            <ChipGroup label="Art" options={TYPES} value={type} onChange={setType} />
          </EditorField>
          <EditorField label="Priorität" hint="bestimmt die Reihenfolge in der Queue">
            <ChipGroup label="Priorität" options={PRIORITIES} value={priority} onChange={setPriority} />
          </EditorField>
        </>
      )}
      <EditorField label="Termin" hint="Pflicht" error={errors.due}>
        <div className={styles.dateRow}>
          <DateTimeBoxes
            label="Termin"
            date={date}
            time={time}
            onDateChange={setDate}
            onTimeChange={setTime}
            invalid={Boolean(errors.due)}
          />
          <span className={styles.dateText}>
            Verschieben setzt einen neuen Termin — die Aufgabe bleibt dieselbe.
          </span>
        </div>
      </EditorField>
      {reschedule ? null : (
        <>
          <EditorField label="Verantwortlich" error={errors.assignee}>
            {assigneeOptions.length <= MAX_CHIPS ? (
              <ChipGroup
                label="Verantwortlich"
                options={assigneeOptions}
                value={assigneeId}
                onChange={setAssigneeId}
              />
            ) : (
              <Select
                label="Verantwortlich"
                options={assigneeOptions}
                value={assigneeId}
                onChange={(event) => setAssigneeId(event.target.value)}
              />
            )}
          </EditorField>
          <EditorField label="Bezug">
            <ChipGroup label="Bezug" options={references} value={reference} onChange={setReference} />
          </EditorField>
        </>
      )}
    </EditorCard>
  );
}
